import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { isAuthError as isAuthErrorUtil, normalizeError } from '@core/utilities/error-handler.util';
import { StatusCodes } from '@core/models';
import { TenantService } from './tenant.service';
import { LoggingService } from './logging.service';
import { FeatureErrorHandler, ErrorContext } from './feature-error-handler.interface';
import { AuthErrorHandlerService } from './auth-error-handler.service';

/**
 * Options for error handling
 */
export interface ErrorHandlerOptions {
  /** Feature name to use for feature-specific handling */
  feature?: string;
  /** Whether to show notification for this error */
  showNotification?: boolean;
  /** Custom error message override */
  customMessage?: string;
}

/**
 * Centralized error handling service that supports both cross-feature
 * and feature-specific error handling strategies.
 */
@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {
  private readonly _router = inject(Router);
  private readonly _tenantService = inject(TenantService);
  private readonly _loggingService = inject(LoggingService);
  private readonly _authErrorHandlerService = inject(AuthErrorHandlerService);

  /** Registry of feature-specific error handlers */
  private readonly _featureHandlers = new Map<string, FeatureErrorHandler>();

  /**
   * Registers a feature-specific error handler.
   * @param feature Feature name (e.g., 'kiosk', 'admin')
   * @param handler Feature error handler implementation
   */
  registerFeature(feature: string, handler: FeatureErrorHandler): void {
    this._featureHandlers.set(feature, handler);
    this._loggingService.debug(`[ErrorHandlerService] Registered error handler for feature: ${feature}`);
  }

  /**
   * Unregisters a feature-specific error handler.
   * @param feature Feature name to unregister
   */
  unregisterFeature(feature: string): void {
    this._featureHandlers.delete(feature);
    this._loggingService.debug(`[ErrorHandlerService] Unregistered error handler for feature: ${feature}`);
  }

  /**
   * Gets the feature handler for a given feature name.
   * @param feature Feature name
   * @returns Feature handler or undefined if not registered
   */
  getFeatureHandler(feature: string): FeatureErrorHandler | undefined {
    return this._featureHandlers.get(feature);
  }

  /**
   * Detects the feature name from the current URL.
   * @param url Current URL (defaults to router URL)
   * @returns Detected feature name or undefined
   */
  detectFeatureFromUrl(url?: string): string | undefined {
    const currentUrl = url || this._router.url;
    
    if (currentUrl.includes('/kiosk')) {
      return 'kiosk';
    } else if (currentUrl.includes('/admin')) {
      return 'admin';
    }
    
    return undefined;
  }

  /**
   * Checks if an error is an authentication/authorization error.
   * @param error Error to check
   * @returns True if error is 401 or 403
   */
  isAuthError(error: unknown): boolean {
    return isAuthErrorUtil(error);
  }

  /**
   * Handles authentication errors (401/403) with feature-specific support.
   * @param error Error to handle
   * @param options Error handling options
   */
  handleAuthError(error: unknown, options?: ErrorHandlerOptions): void {
    const normalized = normalizeError(error);
    
    if (!normalized.isAuthError) {
      this._loggingService.warn('[ErrorHandlerService] handleAuthError called with non-auth error');
      return;
    }

    const currentUrl = this._router.url;
    const tenantId = this._tenantService.currentUriTenant();
    const feature = options?.feature || this.detectFeatureFromUrl(currentUrl);

    const context: ErrorContext = {
      currentUrl,
      tenantId: tenantId || '',
      originalError: error
    };

    // Use feature-specific handler if available
    if (feature) {
      const featureHandler = this._featureHandlers.get(feature);
      if (featureHandler && normalized.statusCode) {
        this._loggingService.debug(`[ErrorHandlerService] Using feature-specific handler for: ${feature}`);
        featureHandler.handleAuthError(context, normalized.statusCode);
        return;
      }
    }

    // Fallback to default auth error handler
    this._loggingService.debug('[ErrorHandlerService] Using default auth error handler');
    if (normalized.statusCode === StatusCodes.Unauthorized) {
      this._authErrorHandlerService.handle401Error();
    } else {
      // For 403, use default handler which will redirect appropriately
      this._authErrorHandlerService.handle401Error();
    }
  }

  /**
   * Handles generic errors with optional feature-specific customization.
   * @param error Error to handle
   * @param options Error handling options
   */
  handleError(error: unknown, options?: ErrorHandlerOptions): void {
    const normalized = normalizeError(error);

    // If it's an auth error, delegate to handleAuthError
    if (normalized.isAuthError) {
      this.handleAuthError(error, options);
      return;
    }

    // Handle other error types
    const message = options?.customMessage || normalized.message;
    this._loggingService.error('[ErrorHandlerService] Unhandled error:', message, error);

    // Additional generic error handling can be added here
    // (e.g., retry logic, specific error type handling, etc.)
  }

  /**
   * Gets the redirect path for a feature when an auth error occurs.
   * @param feature Feature name
   * @param tenantId Tenant ID
   * @returns Redirect path or undefined if feature not registered
   */
  getRedirectPath(feature: string, tenantId: string): string | undefined {
    const handler = this._featureHandlers.get(feature);
    return handler?.getRedirectPath(tenantId);
  }
}
